import { PaginatorInfo } from "./generic.dto";

export enum AdjustmentType {
	INCREASE = "INCREASE",
	DECREASE = "DECREASE",
}

/**
 * NOTE: `PPROCESSED_SUCCESSFULLY` mirrors the (misspelled) value
 * returned by the backend and must be used verbatim.
 */
export enum SalaryAdjustmentStatus {
	PENDING = "PENDING",
	PROCESSING = "PROCESSING",
	PPROCESSED_SUCCESSFULLY = "PPROCESSED_SUCCESSFULLY",
	FAILED_PROCESSING = "FAILED_PROCESSING",
	REVERSED = "REVERSED",
	EXPIRED = "EXPIRED",
}

export type SalaryAdjustmentActor = {
	userId: number | string;
	username: string;
	employeeId?: number;
	name: string;
	category: string;
	platformUser: boolean;
};

type SalaryAdjustment = {
	id: number;
	companyId: number;
	payPeriodId: number | null;
	/** Decimal serialized as string, e.g. "150.0000" */
	adjustmentAmount: string | null;
	/** Fraction serialized as string, "0.1" = 10% */
	adjustmentPercent: string | null;
	adjustmentType: AdjustmentType;
	/** Employee IDs. Older rows hold a JSON string like "[1,2]" */
	notificationRecipients: number[] | string | null;
	code: string;
	name: string;
	description: string;
	status: SalaryAdjustmentStatus;
	actor: SalaryAdjustmentActor;
	/** Failure reason when status is FAILED_PROCESSING */
	report: string | null;
	appliedAt: string | null;
	reversedAt: string | null;
	reversedBy: SalaryAdjustmentActor | null;
	/** Newline-separated list of items skipped during reversal */
	reversalReport: string | null;
	createdAt: string;
	modifiedAt: string | null;
};

export interface SalaryAdjustmentDto extends SalaryAdjustment {}

export class CreateSalaryAdjustmentDto {
	payPeriodId?: number;
	adjustmentAmount?: number;
	/** Fraction, e.g. 0.1 = 10% */
	adjustmentPercent?: number;
	notificationRecipients?: number[];
	adjustmentType!: AdjustmentType;
	code!: string;
	description?: string;
	name!: string;
}

export class UpdateSalaryAdjustmentDto {
	payPeriodId?: number;
	adjustmentAmount?: number;
	adjustmentPercent?: number;
	notificationRecipients?: number[];
	adjustmentType?: AdjustmentType;
	code?: string;
	description?: string;
	name?: string;
}

export enum SalaryAdjustmentOrderBy {
	CREATED_AT_ASC = "createdAt:asc",
	CREATED_AT_DESC = "createdAt:desc",
	MODIFIED_AT_ASC = "modifiedAt:asc",
	MODIFIED_AT_DESC = "modifiedAt:desc",
}

export class QuerySalaryAdjustmentDto {
	payPeriodId?: number;
	page?: number = 1;
	limit?: number;
	orderBy?: SalaryAdjustmentOrderBy = SalaryAdjustmentOrderBy.CREATED_AT_DESC;
}

export interface SalaryAdjustmentPaginator
	extends PaginatorInfo<SalaryAdjustmentDto> {}

/** Parses `notificationRecipients`, which may be a legacy JSON string */
export const parseNotificationRecipients = (
	value: SalaryAdjustmentDto["notificationRecipients"],
): number[] => {
	if (!value) return [];
	if (Array.isArray(value)) return value.map(Number);
	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed.map(Number) : [];
	} catch {
		return [];
	}
};
